import { motion } from 'framer-motion'
import { Plus, List, ChartLineUp, Gear, Tag, Bank, TreeStructure, ShoppingCart, User, Cube, Wrench, SignOut, Globe, CloudArrowUp } from '@phosphor-icons/react'
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useTheme } from '../contexts/ThemeContext'
import { TokenBalance } from './TokenBalance'
import { CustomConnectButton } from './CustomConnectButton'
import { ThemeToggle } from './ThemeToggle'
import { useAdmin } from '../hooks/useAdmin'
import { useLogout } from '../hooks/useLogout'
import { ADDRESSES } from '../config'

const menuItems = [
  { id: 'products', icon: Cube, label: 'Products', title: 'Products' },
  { id: 'accounts', icon: User, label: 'My Accounts', title: 'View My Accounts & Bonds' },
  { id: 'marketplace', icon: ShoppingCart, label: 'Marketplace', title: 'Marketplace' },
  { id: 'create', icon: Plus, label: 'Create', title: 'Create', adminOnly: false },
  { id: 'system', icon: ChartLineUp, label: 'System Status', title: 'System Status', adminOnly: true },
  { id: 'trinity-master', icon: TreeStructure, label: 'Trinity Master', title: 'Trinity Master Admin', adminOnly: true },
  { id: 'admin', icon: Wrench, label: 'Admin Panel', title: 'Admin Settings', adminOnly: true },
]

export function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { theme, toggleTheme } = useTheme()
  const { isAdmin, adminEnabled } = useAdmin()
  const logout = useLogout()

  // Define button styles based on theme
  const getButtonStyle = (isSelected: boolean) => {
    if (isSelected) return theme === 'dark' 
      ? 'bg-transparent text-amber-400'
      : 'bg-transparent text-amber-600'
    
    return theme === 'dark' 
      ? 'bg-transparent text-white/40 hover:text-white'
      : 'bg-transparent text-slate-400 hover:text-slate-900'
  }

  // Redirect from admin pages when admin mode is disabled
  const currentPath = location.pathname.substring(1); // Remove leading slash
  const currentItem = menuItems.find(item => item.id === currentPath);
  console.log("AppLayout currentItem?.adminOnly", currentItem?.adminOnly);
  console.log("AppLayout isAdmin", isAdmin);
  console.log("AppLayout adminEnabled", adminEnabled);
  console.log("AppLayout currentPath", currentPath); 
  if (currentItem?.adminOnly && !adminEnabled) {
    return <Navigate to="/" replace />;
  }

  // Filter menu items based on admin status
  const visibleMenuItems = menuItems.filter(item => {
    if (item.adminOnly) {
      return adminEnabled;
    }
    return true;
  });

  return (
    <>
      {/* Background gradient */}
      <div className={`fixed inset-0 -z-10 ${theme === 'dark' ? 'bg-black/30' : 'bg-white/50'}`}>
        <div className={`absolute inset-0 ${
          theme === 'dark' 
            ? 'bg-[radial-gradient(circle_at_20%_10%,_rgba(16,185,129,0.15)_0%,_transparent_50%),radial-gradient(circle_at_80%_20%,_rgba(59,130,246,0.15)_0%,_transparent_50%),radial-gradient(circle_at_40%_40%,_rgba(234,179,8,0.15)_0%,_transparent_50%)]'
            : 'bg-[radial-gradient(circle_at_20%_10%,_rgba(147,197,183,0.5)_0%,_transparent_50%),radial-gradient(circle_at_80%_20%,_rgba(147,183,197,0.5)_0%,_transparent_50%),radial-gradient(circle_at_40%_40%,_rgba(197,183,147,0.5)_0%,_transparent_50%)]'
        }`}></div>
      </div>

      <header className="p-3 mb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span 
              className="brand-symbol text-secondary cursor-pointer text-xl" 
              onClick={() => navigate('/')}
              title="Return to Home"
            >
              †
            </span>
            <div className="flex items-center gap-2">
              <span className="brand-title text-lg">
                <span className="text-emerald-500">rini</span>†y
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
            <CustomConnectButton />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={logout}
              className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors flex items-center gap-2"
              title="Logout"
            >
              <SignOut size={20} />
              <span className="sr-only">Logout</span>
            </motion.button>
          </div>
        </div>
      </header>

      <nav className="py-2 px-2 mb-3">
        <div className="flex justify-between items-center">
          <ul className="flex flex-wrap gap-3">
            {visibleMenuItems.map((item) => (
              <motion.li
                key={item.id}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <button
                  onClick={() => navigate(`/${item.id}`)}
                  className={`p-1 rounded-md transition-all h-8 w-8 flex items-center justify-center ${
                    getButtonStyle(location.pathname === `/${item.id}`)
                  }`}
                  title={item.title}
                >
                  <item.icon 
                    size={location.pathname === `/${item.id}` ? 32 : 28} 
                    weight={location.pathname === `/${item.id}` ? "fill" : "regular"}
                  />
                  <span className="sr-only">{item.label}</span>
                </button>
              </motion.li>
            ))}
          </ul>
          <div className="flex items-center gap-2">
              <TokenBalance tokenAddress={ADDRESSES.TRINITY_TOKEN} symbol="†" />
          </div>
        </div>
      </nav>

      <main className="p-3">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -15 }}
          transition={{ duration: 0.2 }}
        >
          <Outlet />
        </motion.div>
      </main>
    </>
  )
} 